import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../SEO'
import { SplitSection, StackRibbon } from '../ServicePage'
import ContactForm from '../ContactForm'
import daznLogo from '../../assets/clientLogos/DAZN_transparent_logo.png'
import { trackCtaClick, trackOutboundClick } from '../../utils/analytics'
import './../../styles/CaseStudy.css'

/* Hero visual — multi-device tiles + globe */
const DaznHero = () => (
  <svg viewBox="0 0 460 460" xmlns="http://www.w3.org/2000/svg" className="cs-hero__svg" aria-hidden="true">
    <defs>
      <linearGradient id="dazn-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0066ff" />
        <stop offset="100%" stopColor="#00d4ff" />
      </linearGradient>
      <radialGradient id="dazn-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#00d4ff" stopOpacity="0.45" />
        <stop offset="100%" stopColor="#00d4ff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="230" cy="230" r="200" fill="url(#dazn-glow)">
      <animate attributeName="r" values="200;215;200" dur="6s" repeatCount="indefinite" />
    </circle>

    {/* TV in centre */}
    <g transform="translate(230 200)">
      <rect x="-110" y="-70" width="220" height="140" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="-100" y="-60" width="200" height="120" rx="6" fill="url(#dazn-grad)" opacity="0.85">
        <animate attributeName="opacity" values="0.7;1;0.7" dur="3s" repeatCount="indefinite" />
      </rect>
      <polygon points="-12,-14 14,0 -12,14" fill="#fff" opacity="0.95" />
      <rect x="-30" y="78" width="60" height="6" rx="3" fill="rgba(255,255,255,0.40)" />
    </g>

    {/* Phone */}
    <g transform="translate(70 130)">
      <rect width="60" height="100" rx="10" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="6" y="10" width="48" height="76" rx="4" fill="url(#dazn-grad)" opacity="0.7" />
      <animateTransform attributeName="transform" type="translate" values="70 130;70 122;70 130" dur="5s" repeatCount="indefinite" />
    </g>

    {/* Console controller */}
    <g transform="translate(355 130)">
      <rect width="64" height="40" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <circle cx="14" cy="20" r="6" fill="rgba(0,212,255,0.65)" />
      <circle cx="50" cy="20" r="6" fill="rgba(0,212,255,0.65)" />
      <animateTransform attributeName="transform" type="translate" values="355 130;355 138;355 130" dur="5.4s" repeatCount="indefinite" />
    </g>

    {/* Tablet */}
    <g transform="translate(80 320)">
      <rect width="74" height="54" rx="6" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.20)" />
      <rect x="6" y="6" width="62" height="42" rx="3" fill="url(#dazn-grad)" opacity="0.6" />
      <animateTransform attributeName="transform" type="translate" values="80 320;80 312;80 320" dur="6s" repeatCount="indefinite" />
    </g>

    {/* Globe icon */}
    <g transform="translate(385 350)">
      <circle r="22" fill="rgba(0,212,255,0.10)" stroke="rgba(0,212,255,0.55)" strokeWidth="1.5" />
      <ellipse cx="0" cy="0" rx="22" ry="9" fill="none" stroke="rgba(0,212,255,0.55)" strokeWidth="1.5" />
      <line x1="0" y1="-22" x2="0" y2="22" stroke="rgba(0,212,255,0.55)" strokeWidth="1.5" />
      <text x="0" y="-30" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight="700" fontSize="10" fill="#00d4ff">200+</text>
    </g>
  </svg>
)

const pressLinks = [
  {
    href: 'https://www.sportsvideo.org/2020/12/04/dazn-launches-in-more-than-200-countries-around-the-globe/',
    src: 'Sports Video Group',
    title: 'DAZN launches in more than 200 countries around the globe',
  },
  {
    href: 'https://deadline.com/2020/10/dazn-global-streaming-sports-200-countries-anthony-joshua-1234602714/',
    src: 'Deadline',
    title: 'DAZN reactivates global expansion plan, updates strategy for 200-country sports streaming launch',
  },
  {
    href: 'https://www.broadbandtvnews.com/2020/10/28/dazn-expands-to-more-than-200-countries-on-december-1/',
    src: 'Broadband TV News',
    title: 'DAZN expands to more than 200 countries on December 1',
  },
]

const DAZN = () => (
  <>
    <SEO
      path="/case-studies/dazn"
      title="DAZN — 200-Country Global Launch, Multi-Device QA"
      description="QA Tech led test automation for DAZN's global launch into more than 200 countries — covering Smart TVs, PlayStation, Xbox, MagentaTV, SkyQ, Amazon Fire and the wider device matrix, plus DRM, region-locks and microservices."
      keywords="DAZN case study, sports streaming QA, multi-device test automation, OTT QA, Smart TV testing, console testing, DRM testing, Widevine testing, PlayReady testing, WebdriverIO, BrowserStack"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: 'DAZN — global 200-country launch QA',
        author: { '@type': 'Organization', name: 'QA Tech' },
        publisher: { '@type': 'Organization', name: 'QA Tech', url: 'https://qatechltd.com' },
        about: ['DAZN', 'Sports streaming', 'Multi-device QA', 'OTT QA'],
        url: 'https://qatechltd.com/case-studies/dazn',
      }}
    />

    <div className="body cs-body">
      <header className="cs-hero">
        <div className="cs-hero__bg" aria-hidden="true">
          <span className="cs-hero__orb cs-hero__orb--a" />
          <span className="cs-hero__orb cs-hero__orb--b" />
          <span className="cs-hero__grid" />
        </div>

        <div className="cs-hero__inner">
          <nav className="sp-breadcrumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link><span aria-hidden="true">/</span>
            <Link to="/case-studies">Case Studies</Link><span aria-hidden="true">/</span>
            <span>DAZN</span>
          </nav>

          <div className="cs-hero__content">
            <div className="cs-hero__client">
              <img src={daznLogo} alt="DAZN" className="cs-hero__logo" loading="lazy" />
              <div className="cs-hero__client-text">
                <span className="eyebrow">Case study</span>
                <h2 className="cs-hero__client-name">DAZN</h2>
                <p className="cs-hero__client-sub">Sports streaming &amp; VOD &mdash; global 200+ country launch</p>
              </div>
            </div>

            <h1 className="cs-hero__title">
              200 countries. Many devices. <span className="cs-hero__title-accent">One launch night.</span>
            </h1>

            <p className="cs-hero__lede">
              We led test automation across DAZN&rsquo;s sports-streaming
              and VOD platform during the run-up to its global launch
              &mdash; the moment it went from regional service to live
              in over 200 countries. Smart TVs, consoles, partner
              platforms, mobile, web &mdash; tested as one programme.
            </p>

            <div className="cs-hero__cta-row">
              <a
                href="#cs-contact"
                className="sp-cta-primary"
                onClick={() => trackCtaClick('Work with us', 'case_study_dazn_hero')}
              >
                Work with us
              </a>
              <a href="#cs-overview" className="sp-cta-secondary">Read the case study &darr;</a>
            </div>
          </div>

          <div className="cs-hero__visual" aria-hidden="true">
            <DaznHero />
          </div>
        </div>
      </header>

      <section className="cs-stats" aria-label="Project at a glance" id="cs-overview">
        <div className="cs-stats__inner">
          <div className="cs-stat"><span className="cs-stat__num">11 months</span><span className="cs-stat__label">engagement (2020 &ndash; 2021)</span></div>
          <div className="cs-stat"><span className="cs-stat__num">200+</span><span className="cs-stat__label">countries at launch</span></div>
          <div className="cs-stat"><span className="cs-stat__num">Multi-device</span><span className="cs-stat__label">TVs, consoles, mobile, web</span></div>
          <div className="cs-stat"><span className="cs-stat__num">All levels</span><span className="cs-stat__label">API, unit &amp; UI automation</span></div>
        </div>
      </section>

      <main className="cs-main">
        <SplitSection index={1} align="left" kicker="The brief" title="One platform, every screen, world-stage launch.">
          <p>
            DAZN is a sports-streaming and video-on-demand platform
            running across an unusually broad set of consumer devices
            &mdash; Smart TVs, PlayStation 4 and 5, Xbox, MagentaTV, SkyQ,
            Amazon Fire &mdash; on a microservice architecture. We were
            part of the team that took it from regional service to
            <strong> live in over 200 countries</strong>.
          </p>
        </SplitSection>

        <SplitSection index={2} align="right" kicker="Test framework" title="One framework, every device.">
          <p>
            We designed and implemented a multi-device test automation
            framework in TypeScript &mdash; combining WebdriverIO, TestCafe
            and Cypress with Cucumber BDD &mdash; running against real
            physical devices and in cloud farms. The same suite, the same
            scenarios, the same reporting, across every screen.
          </p>
          <ul>
            <li>WebdriverIO + TestCafe + Cypress</li>
            <li>Cucumber BDD for shared specs</li>
            <li>BrowserStack for cloud devices</li>
            <li>Percy.io for visual regression</li>
          </ul>
        </SplitSection>

        <SplitSection index={3} align="left" kicker="Release process" title="Set up. Defined. Repeatable.">
          <p>
            We set up and implemented the release process used through
            launch &mdash; how regression got run, how decisions got made,
            who signed off what, and how to do all of that fast enough to
            ship to 200 countries on schedule.
          </p>
        </SplitSection>

        <SplitSection index={4} align="right" kicker="DRM &amp; rights" title="Widevine, PlayReady, region-locks.">
          <p>
            Streaming live sport at this scale means <strong>DRM</strong>
            and <strong>licence testing</strong> are the centre of gravity.
            We tested content delivery against Widevine and PlayReady, plus
            the regional and age-restriction rules that decide who can
            watch what, where, when. Charles and Fiddler proxied real
            devices to give us full visibility of the negotiation.
          </p>
        </SplitSection>

        <SplitSection index={5} align="left" kicker="All-levels QA" title="API, unit, UI — test everywhere.">
          <p>
            Automation across all three test levels &mdash; unit (Jest
            assertions), API (microservice contract checks), UI (the
            multi-device suite). Manual UAT and release testing alongside
            automation, plus test strategy implementation and developer
            education on writing their own tests.
          </p>
        </SplitSection>

        <SplitSection index={6} align="right" kicker="DevOps" title="CI pipelines feeding the release.">
          <p>
            DevOps work to wire the automated suites into the CI pipeline
            so the team had a clear, fast signal on every change &mdash;
            stable enough to trust during the highest-pressure release of
            the year.
          </p>
        </SplitSection>

        <SplitSection index={7} align="left" kicker="Test management" title="TestRail, alongside everything else.">
          <p>
            Used TestRail for test management across the programme &mdash;
            test cases, suites, runs and traceability all stayed visible
            to product, engineering and leadership during the run-up to
            launch.
          </p>
        </SplitSection>

        <StackRibbon
          label="What we delivered"
          items={[
            'Multi-device test framework', 'Smart TV / console / mobile / web automation',
            'Release process design &amp; execution',
            'DRM &amp; licence testing (Widevine, PlayReady)',
            'Region- &amp; age-restriction testing',
            'API / unit / UI automation', 'BrowserStack integration',
            'Percy.io visual regression', 'Test strategy &amp; developer enablement',
            'CI/CD pipeline integration', 'TestRail management',
          ]}
        />

        <StackRibbon
          label="Tech &amp; tools"
          items={[
            'TypeScript', 'JavaScript',
            'WebdriverIO', 'TestCafe', 'Cypress', 'Cucumber',
            'Jest',
            'BrowserStack', 'Percy.io',
            'TestRail',
            'Widevine', 'PlayReady',
            'Charles', 'Fiddler',
          ]}
        />

        <section className="cs-press" aria-label="Press and coverage">
          <div className="cs-press__inner">
            <span className="sp-stack__label">Press &amp; coverage</span>
            <p className="cs-press__lead">
              Selected coverage of the DAZN 200-country global launch.
            </p>
            <ul className="cs-press__list">
              {pressLinks.map((p) => (
                <li key={p.href}>
                  <a href={p.href} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackOutboundClick(p.href, p.title, 'case_study_dazn_press')}>
                    <span className="cs-press__src">{p.src}</span>
                    <span className="cs-press__title">{p.title}</span>
                  </a>
                </li>
              ))}
            </ul>
            <p className="cs-press__note">
              External links open in a new tab. QA Tech is independent and
              not affiliated with DAZN.
            </p>
          </div>
        </section>
      </main>

      <section className="sp-cta-section">
        <div className="sp-cta-section__inner">
          <h2>Big-bang launch on the calendar?</h2>
          <p>If your product has to ship clean across markets and devices, we&rsquo;d love to talk.</p>
        </div>
      </section>

      <div id="cs-contact"><ContactForm /></div>
    </div>
  </>
)

export default DAZN
